import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { X, Pin, PinOff, Send } from "lucide-react";

import ClubChip from "./ClubChip";
import StatusPill from "./StatusPill";
import { modeLabel } from "../../../constants/taskboardStatus";
import { taskboard } from "../../../services/taskboardService";

// Entry detail drawer with the append-only update thread (product rule
// 4). The original note sits on top and is NEVER mutated by updates;
// updates append below with author + timestamp; exactly one can be
// pinned. Adding an update and pinning persist via the update
// subresource; the parent refetches so board/timeline stay in sync.
function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

function fmt(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const UpdateRow = ({ update, canModify, onTogglePin }) => (
  <div className={`rounded-lg p-3 ${update.pinned ? "bg-[#FFF8EB] border border-[#F4D8A6]" : "bg-[#F8FAFC] border border-[#EAECF0]"}`}>
    <div className="flex items-center gap-2 mb-1">
      <span className="w-5 h-5 rounded-full bg-[#FDEAD7] text-[#B93815] flex items-center justify-center text-[8px] font-bold flex-none">
        {initials(update.author?.name)}
      </span>
      <span className="text-[12px] font-semibold text-[#344054]">{update.author?.name || "-"}</span>
      <span className="text-[11px] text-[#98A2B3]">{fmt(update.created_at)}</span>
      {update.pinned && <span className="text-[10px] font-semibold text-[#B54708] uppercase tracking-wide">Pinned</span>}
      {canModify && (
        <button
          type="button"
          onClick={() => onTogglePin(update)}
          className="ml-auto text-[#98A2B3] hover:text-[#B54708]"
          title={update.pinned ? "Unpin" : "Pin to top"}
        >
          {update.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
    <p className="text-[13px] text-[#475467] whitespace-pre-wrap">{update.text}</p>
  </div>
);

const EntryThreadDrawer = ({ entry, folderId, canModify = false, onClose, onChanged, onStatusChange }) => {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const textareaRef = useRef(null);

  // Open straight into "ready to type" so adding a note is one click + type
  // (the whole reason the drawer opens from the card's "Add update").
  useEffect(() => {
    const id = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  if (!entry) return null;
  const updates = entry.updates || [];
  const pinned = updates.filter((u) => u.pinned);
  const rest = updates
    .filter((u) => !u.pinned)
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));

  const addUpdate = async () => {
    if (!text.trim()) return;
    setPosting(true);
    const res = await taskboard.addUpdate(folderId, entry.id, { text: text.trim() });
    setPosting(false);
    if (!res.success) {
      toast.error(res.error || "Failed to add update");
      return;
    }
    setText("");
    toast.success("Update added");
    onChanged && onChanged();
  };

  const togglePin = async (update) => {
    const res = await taskboard.pinUpdate(folderId, entry.id, update.id, !update.pinned);
    if (!res.success) {
      toast.error(res.error || "Failed to pin");
      return;
    }
    onChanged && onChanged();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md h-full flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#EAECF0]">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-[#101828]">{modeLabel(entry.mode_of_communication)}</span>
            <StatusPill
              status={entry.status}
              readOnly={!canModify}
              title={!canModify ? "Only the entry's agent or an agency admin can change this entry." : undefined}
              onChange={(s) => onStatusChange && onStatusChange(entry, s)}
            />
          </div>
          <button onClick={onClose} className="text-[#98A2B3] hover:text-[#475467]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
          {/* Original note (never mutated) */}
          <div className="rounded-lg border border-[#E4E9F0] p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#98A2B3]">Original</span>
              <span className="text-[11px] text-[#98A2B3]">{entry.date}</span>
              {entry.agent?.name && <span className="text-[11px] text-[#98A2B3]">· {entry.agent.name}</span>}
            </div>
            <p className="text-[13.5px] text-[#101828] whitespace-pre-wrap mb-2">{entry.notes}</p>
            <ClubChip entry={entry} />
          </div>

          {/* Thread */}
          {updates.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-[12px] font-semibold text-[#475467]">
                {updates.length} {updates.length === 1 ? "update" : "updates"}
              </span>
              {pinned.map((u) => (
                <UpdateRow key={u.id} update={u} canModify={canModify} onTogglePin={togglePin} />
              ))}
              {rest.map((u) => (
                <UpdateRow key={u.id} update={u} canModify={canModify} onTogglePin={togglePin} />
              ))}
            </div>
          )}

          {updates.length === 0 && (
            <p className="text-[12.5px] text-[#98A2B3] text-center py-2">
              No updates yet, add the first note below.
            </p>
          )}
        </div>

        {/* Add update */}
        <div className="border-t border-[#EAECF0] p-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              placeholder="Add an update… (the original note stays untouched)"
              className="flex-1 resize-none border border-[#E4E9F0] rounded-lg px-3 py-2 text-[13px] outline-none focus:ring-1 focus:ring-[#2283FF]"
            />
            <button
              type="button"
              onClick={addUpdate}
              disabled={posting || !text.trim()}
              className="flex items-center gap-1.5 bg-[#2283FF] text-white rounded-lg px-3 py-2 text-[13px] font-semibold disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" />
              {posting ? "…" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EntryThreadDrawer;
